import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const ADMIN_API = `${BACKEND_URL}/api/admin`;

const AdminAuthContext = createContext(null);

// withCredentials so the browser sends/receives the httpOnly admin_token
// cookie set by the backend on /login. The frontend never reads, stores
// or attaches this token itself (no localStorage/sessionStorage), which
// significantly reduces its exposure to theft via XSS compared to the
// previous localStorage-based token storage.
export const adminApi = axios.create({ baseURL: ADMIN_API, withCredentials: true });

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await adminApi.get("/me");
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const res = await adminApi.post("/login", { email, password });
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminApi.post("/logout");
    } finally {
      setUser(null);
    }
  }, []);

  const can = useCallback(
    (module, level = "read") => {
      if (!user) return false;
      if (user.is_owner) return true;
      const granted = user.permissions?.[module] || "none";
      const levels = { none: 0, read: 1, write: 2 };
      return levels[granted] >= levels[level];
    },
    [user]
  );

  // Memoized so consumers don't re-render on every AdminAuthProvider
  // render from a brand-new object identity -- only when one of these
  // values actually changes.
  const contextValue = useMemo(
    () => ({ user, loading, login, logout, can }),
    [user, loading, login, logout, can]
  );

  return <AdminAuthContext.Provider value={contextValue}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => useContext(AdminAuthContext);
